import '../App.css';
import {
    Box, Stack,
    CircularProgress } from '@mui/material';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import PullToRefresh from '../Services/PullToRefresh';
import { useNavigate } from 'react-router-dom';
import { useLSContext } from '../Context/useLSContext';
import { LockScreen } from './LockScreen';
import { ScoringView } from './ScoringView';
import { NavigationBar } from './NavigationBar';
import { useDataContext } from '../Context/useDataContext';
import { GroupScorerConfirmation } from './GroupScorerConfirmation';
import { FirstTeeDrawer } from './FirstTeeDrawer';
import { currentRoundScoringStarted } from '../utils/currentRoundScoringStarted';
import { resolvedStartHole } from '@moment-golf/shared';

interface ScoringContentProps {
    fetchScoringData: (scoreCodeParam: string, checkCookies: boolean) =>
        Promise<{ success: boolean; requiresGroupSelection?: boolean; eventID: string; scoreCode: string }>;
}

export const ScoringContent = ({ fetchScoringData }: ScoringContentProps) => {

    const navigate = useNavigate(); // Initialize the navigate function

    const { windowSize, scoreLock, toggleScoring, setToggleScoring, isPWA } = useLSContext();
    const { courseData, eventData, registrationData,
         scoreCode, scoreData, currentRound, groupConfirmResult, setGroupConfirmResult, groupScorer, setGroupScorer } = useDataContext();
    const stackRef = useRef<HTMLDivElement>(null);

    const [appLoading, setAppLoading] = useState(true);
    const [groupConfirmOpen, setGroupConfirmOpen] = useState(false);
    const [fadeIn, setFadeIn] = useState(false);
    const [firstTeeDrawerOpen, setFirstTeeDrawerOpen] = useState(false);

    // console.log('Score Lock:', scoreLock);

    useEffect(() => {
        // Set fadeIn to true after a short delay to trigger the CSS transition
        setTimeout(() => {
            setFadeIn(true);
            setToggleScoring(false);
        }, 100);
    }, []);
    

    useEffect(() => {

        // console.log('Group Check | confirmResult:', groupConfirmResult, 'currentRound:', currentRound);
        if (scoreData.length > 0 && !groupConfirmResult) {
            if (eventData.scoreType.toLowerCase() !== 'opponent') {

                // console.log('Group Check | scoreData:', scoreData);
                const checkGroupScorer = scoreData.every((score: any) => 
                    score.roundScores[currentRound-1].markerScoreCode === scoreCode);

                // console.log('Group Check | checkGroupScorer:', checkGroupScorer);

                if (currentRoundScoringStarted(scoreData, currentRound, eventData)) {
                    setGroupConfirmResult(true);
                    setGroupScorer(checkGroupScorer);
                    return;
                }

                if (!checkGroupScorer) {
                    setGroupConfirmOpen(true);
                }
                else {
                    setGroupConfirmResult(true);
                    setGroupScorer(true);
                }
            }
        }
    }, [scoreData, currentRound, eventData, scoreCode, groupConfirmResult, setGroupConfirmResult, setGroupScorer]);

    // Close tee-set drawer if the round has started (entered scores)
    useEffect(() => {
        if (currentRoundScoringStarted(scoreData, currentRound, eventData)) {
            setFirstTeeDrawerOpen(false);
        }
    }, [scoreData, currentRound, eventData]);

    // Open FirstTeeDrawer for tee-set-only self-selection after the scorer confirms as group scorer
    useEffect(() => {
        if (
            groupConfirmResult &&
            groupScorer &&
            eventData?.teeSelfSelection &&
            !eventData?.groupSelfSelection &&
            !currentRoundScoringStarted(scoreData, currentRound, eventData)
        ) {
            setFirstTeeDrawerOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- open when user finishes group flow; snapshot scoreData/eventData at that time
    }, [groupConfirmResult, groupScorer]);

    const firstTeeStartingHole = useMemo(() => {
        const fromScore = scoreData[0]?.roundScores?.[currentRound - 1]?.startingHole;
        if (fromScore != null && Number(fromScore) > 0 && Number.isFinite(Number(fromScore))) {
            return Math.round(Number(fromScore));
        }
        const roundCfg = eventData?.rounds?.find((r: any) => Number(r?.roundNumber) === Number(currentRound))
            ?? eventData?.rounds?.[currentRound - 1];
        return resolvedStartHole(roundCfg);
    }, [scoreData, currentRound, eventData]);

    // Start up of the app with direct hard refresh, sends back to login
    useEffect(() => {

        //console.log('Scoring Content mounted. I have scoreData of:', scoreData.length);

        if (scoreData.length > 0) {
            setAppLoading(false);
        } else {
            navigate('/login/');
        }

        setTimeout(() => {
            setAppLoading(false);
        }, 1000);

    }, [scoreData, setAppLoading]); // Empty dependency array - this effect runs only once when component mounts

    return (
        <Box sx={{ 
            width: '100%', 
            height: '100%',
            overflow: 'hidden',
            position: 'fixed',
            top: 0,
            left: 0,
            overscrollBehavior: 'none'
        }}
        className="live-scoring">
        <PullToRefresh 
            threshold={80} 
            resistance={2}  
            containerRef={stackRef as React.RefObject<HTMLDivElement>} 
            onRefresh={() => {
                fetchScoringData(scoreCode, true);
                return Promise.resolve();
            }}>
            {appLoading ? 
                ( 
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', 
                        height: `calc(100dvh - ${windowSize.appBarHeight}px)`, }}>
                        <CircularProgress />
                    </Box>
                )
            : 
                (
                    <Stack 
                        direction={'column'} 
                        //ref={stackRef}
                        sx={{
                            //border: '1px solid red',
                            gap: 0,
                            flex: 1,
                            backgroundColor: 'sepia.background',
                            borderRadius: 0,
                            justifyContent: 'flex-start',
                            overflowY: 'auto',
                            maxHeight: `calc(100dvh - ${windowSize.appBarHeight + (isPWA ? 30 : 20)}px - env(safe-area-inset-bottom))`,
                            marginTop: `${windowSize.appBarHeight}px`,
                            paddingBottom: '12px',
                            WebkitOverflowScrolling: 'touch',
                            touchAction: 'pan-y',
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none',
                            overscrollBehavior: 'contain',
                            '&::-webkit-scrollbar': {
                                display: 'none'
                            },
                            opacity: fadeIn ? 1 : 0,
                            transition: 'opacity 0.4s ease-in-out',
                        }}>
                        {scoreLock && !toggleScoring ?
                            <LockScreen />
                        :
                            <>
                                <GroupScorerConfirmation open={groupConfirmOpen} setOpen={setGroupConfirmOpen} />
                                <FirstTeeDrawer
                                    open={firstTeeDrawerOpen}
                                    onClose={() => setFirstTeeDrawerOpen(false)}
                                    eventData={eventData}
                                    courseData={courseData}
                                    registrationData={registrationData}
                                    currentRound={currentRound}
                                    currentTeeTime={scoreData[0]?.roundScores?.[currentRound - 1]?.teeTime ?? new Date()}
                                    startingHole={firstTeeStartingHole}
                                    scoreCode={scoreCode}
                                    onComplete={() => {
                                        setFirstTeeDrawerOpen(false);
                                        fetchScoringData(scoreCode, false);
                                    }}
                                />
                                <Box sx={{ py: '10px' }}>
                                    <NavigationBar />
                                </Box>
                                <ScoringView stackRef={stackRef} />
                                
                            </>
                        }
                    </Stack>
                )}
        </PullToRefresh>
        </Box>
    )
}